import { all, fork, put, takeEvery, call } from "redux-saga/effects";
import { SagaIterator } from "@redux-saga/core";
import { APICore } from "../../helpers/api/apiCore";
import {
  login as loginApi,
  logout as logoutApi,
  signup as signupApi,
  forgotPassword as forgotPasswordApi,
} from "../../helpers/";
import { authApiResponseSuccess, authApiResponseError } from "./actions";
import { AuthActionTypes } from "./constants";

interface UserData {
  payload: {
    _id?: string;
    fullname?: string;
    name: string;
    password?: string;
    email: string;
    phone: string;
    role: string;
    token?: string;
  };
  type: string;
}

const api = new APICore();

// Normalise l'erreur remontée par axios en un payload exploitable par les pages
// auth : soit le corps JSON du back tel quel (`{ error, message, lockoutRemainingMs, ... }`),
// soit une string prête à afficher (network error, dev-only). Les composants
// `<AuthErrorAlert>` traduisent les codes connus (EMAIL_NOT_VERIFIED, lockout, …).
function extractAuthErrorPayload(error: any): any {
  const isBackendUnreachable =
    error?.code === "ECONNREFUSED" ||
    (typeof error?.message === "string" &&
      (error.message.includes("ECONNREFUSED") ||
        error.message.includes("Network Error") ||
        error.message.includes("proxy")));
  if (isBackendUnreachable) {
    return process.env.NODE_ENV === "development"
      ? "Backend not running. Start it: npm run start:backend (from project root) or: cd back && npm run build && node build/app.js"
      : { error: "__BACKEND_UNREACHABLE__" };
  }
  const data = error?.response?.data;
  if (data && typeof data === "object") return data;
  if (typeof data === "string") return { error: data };
  if (typeof error === "string") return { error };
  return { error: error?.message || "__GENERIC_ERROR__" };
}

function* login({ payload: { email, password } }: UserData): SagaIterator {
  try {
    const response = yield call(loginApi, { email, password });
    const user = response.data;
    api.setLoggedInUser(user);
    yield put(authApiResponseSuccess(AuthActionTypes.LOGIN_USER, user));
  } catch (error: any) {
    yield put(authApiResponseError(AuthActionTypes.LOGIN_USER, extractAuthErrorPayload(error)));
    api.setLoggedInUser(null);
  }
}

function* logout(): SagaIterator {
  try {
    yield call(logoutApi);
    api.setLoggedInUser(null);
    yield put(authApiResponseSuccess(AuthActionTypes.LOGOUT_USER, {}));
  } catch (error: any) {
    api.setLoggedInUser(null);
    yield put(authApiResponseSuccess(AuthActionTypes.LOGOUT_USER, {}));
  }
}

function* signup({
  payload: { name, email, phone, password },
}: UserData): SagaIterator {
  try {
    const response = yield call(signupApi, { name, email, phone, password });
    const user = response.data;
    yield put(authApiResponseSuccess(AuthActionTypes.SIGNUP_USER, user));
  } catch (error: any) {
    yield put(authApiResponseError(AuthActionTypes.SIGNUP_USER, extractAuthErrorPayload(error)));
    api.setLoggedInUser(null);
  }
}

function* forgotPassword({ payload: { email } }: UserData): SagaIterator {
  try {
    const response = yield call(forgotPasswordApi, { email });
    yield put(
      authApiResponseSuccess(AuthActionTypes.FORGOT_PASSWORD, response.data)
    );
  } catch (error: any) {
    yield put(authApiResponseError(AuthActionTypes.FORGOT_PASSWORD, error));
  }
}

export function* watchLoginUser() {
  yield takeEvery(AuthActionTypes.LOGIN_USER, login);
}

export function* watchLogout() {
  yield takeEvery(AuthActionTypes.LOGOUT_USER, logout);
}

export function* watchSignup(): any {
  yield takeEvery(AuthActionTypes.SIGNUP_USER, signup);
}

export function* watchForgotPassword(): any {
  yield takeEvery(AuthActionTypes.FORGOT_PASSWORD, forgotPassword);
}

function* authSaga() {
  yield all([
    fork(watchLoginUser),
    fork(watchLogout),
    fork(watchSignup),
    fork(watchForgotPassword),
  ]);
}

export default authSaga;
